/**
 * Money handling.
 *
 * Every amount in this application is an integer number of *sen* (1/100 MYR).
 * Floating point is never used to store or accumulate money. The only place a
 * float may appear is at the edge of the system, when a human-typed decimal
 * string is converted to sen -- and that conversion is done on the string, not
 * with `parseFloat` arithmetic.
 */

export const SEN_PER_UNIT = 100;

export class MoneyParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyParseError';
  }
}

/** True when the value is a safe, whole number of sen. */
export function isValidSen(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

export function assertSen(value: unknown, label = 'amount'): number {
  if (!isValidSen(value)) {
    throw new MoneyParseError(`${label} must be a whole number of sen, received: ${String(value)}`);
  }
  return value;
}

/**
 * Parse a human amount string into integer sen.
 *
 * Accepts: "12", "12.9", "12.90", "RM 12.90", "RM12.90", "1,234.50", "-12.90",
 * "(12.90)" (accounting negative), "12.90 MYR".
 * Rejects: empty strings, more than two decimal places, anything non-numeric.
 */
export function parseAmountToSen(input: string | number): number {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) throw new MoneyParseError('Amount is not a finite number');
    // A number input is interpreted as major units (e.g. 12.9 => 1290 sen).
    return decimalStringToSen(input.toFixed(10).replace(/0+$/, '').replace(/\.$/, ''));
  }

  let text = input.trim();
  if (!text) throw new MoneyParseError('Amount is empty');

  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1).trim();
  }

  // Strip currency markers and spaces. Only MYR markers are accepted here;
  // other currencies are handled by the caller before reaching this function.
  // No trailing \b: "RM12.90" has no boundary between "M" and "1".
  text = text.replace(/\b(?:RM|MYR)/gi, '').replace(/\s+/g, '');

  if (text.startsWith('-')) {
    negative = !negative;
    text = text.slice(1);
  } else if (text.startsWith('+')) {
    text = text.slice(1);
  }

  /**
   * Thousands separators, but only where a comma can only mean one thing.
   *
   * A comma is a thousands separator in Malaysia and a decimal separator in
   * much of Europe, and a phone set to a European locale can hand back "53,00"
   * meaning fifty-three ringgit. Stripping every comma would read that as
   * RM 530,000 -- a silent hundred-fold error, which is the worst kind.
   *
   * A real thousands group is always exactly three digits, so a comma followed
   * by three digits is unambiguous and anything else is refused.
   */
  if (text.includes(',')) {
    if (/^\d{1,3}(,\d{3})*(\.\d{1,2})?$/.test(text)) {
      text = text.replace(/,/g, '');
    } else {
      throw new MoneyParseError(
        `"${input}" is ambiguous: use a full stop for the decimal point, as in 53.00`,
      );
    }
  }

  // A specific complaint beats a generic one: three decimal places is the most
  // common way a typed amount goes wrong, and "too many decimals" tells the
  // person what to change.
  if (/^\d+\.\d{3,}$/.test(text)) {
    throw new MoneyParseError('Amounts cannot have more than two decimal places');
  }

  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    throw new MoneyParseError(`Cannot read "${input}" as a Ringgit amount`);
  }

  const sen = decimalStringToSen(text);
  return negative ? -sen : sen;
}

/** Convert a plain "123.45" style string into integer sen without float maths. */
function decimalStringToSen(text: string): number {
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [whole = '0', fraction = ''] = body.split('.');
  if (fraction.length > 2) {
    throw new MoneyParseError('Amounts cannot have more than two decimal places');
  }
  const padded = (fraction + '00').slice(0, 2);
  const sen = Number(whole) * SEN_PER_UNIT + Number(padded);
  if (!Number.isSafeInteger(sen)) {
    throw new MoneyParseError('Amount is too large');
  }
  return negative ? -sen : sen;
}

/** "1234" -> "12.34". Always two decimal places, never a float round-trip. */
export function senToDecimalString(sen: number): string {
  assertSen(sen);
  const negative = sen < 0;
  const abs = Math.abs(sen);
  const whole = Math.trunc(abs / SEN_PER_UNIT);
  const rest = abs % SEN_PER_UNIT;
  return `${negative ? '-' : ''}${whole}.${String(rest).padStart(2, '0')}`;
}

export interface FormatMoneyOptions {
  /** Include the currency prefix. Default true. */
  currency?: string;
  /** Render 123456789 sen as "RM 1,234,567.89". Default true. */
  grouped?: boolean;
  /** Show a leading "+" for positive values. Default false. */
  signed?: boolean;
}

/** Format sen the way it is written in Malaysia: `RM 12.90`. */
export function formatMoney(sen: number, options: FormatMoneyOptions = {}): string {
  const { currency = 'MYR', grouped = true, signed = false } = options;
  assertSen(sen);
  const negative = sen < 0;
  const decimal = senToDecimalString(Math.abs(sen));
  const [whole = '0', fraction = '00'] = decimal.split('.');
  const wholeOut = grouped ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : whole;
  const symbol = currency === 'MYR' ? 'RM' : currency;
  const sign = negative ? '-' : signed ? '+' : '';
  return `${sign}${symbol} ${wholeOut}.${fraction}`;
}

/** Sum sen values with an integer-only guarantee. */
export function sumSen(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += assertSen(value);
  }
  if (!Number.isSafeInteger(total)) throw new MoneyParseError('Total overflowed safe integer range');
  return total;
}

/**
 * Split `sen` into `parts` whole-sen pieces, distributing the remainder to the
 * earliest parts so the pieces always add back up to the original.
 */
export function splitSen(sen: number, parts: number): number[] {
  assertSen(sen);
  if (!Number.isInteger(parts) || parts <= 0) throw new MoneyParseError('parts must be >= 1');
  const base = Math.trunc(sen / parts);
  const remainder = sen - base * parts;
  const step = remainder >= 0 ? 1 : -1;
  let left = Math.abs(remainder);
  return Array.from({ length: parts }, (_unused, index) => {
    if (left > 0 && index < Math.abs(remainder)) {
      left -= 1;
      return base + step;
    }
    return base;
  });
}

/** Percentage of `part` within `total`, rounded to one decimal, safe at total=0. */
export function percentOf(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}
